import React, { useState, useEffect } from 'react';
import { Save, Edit, Package, Plus, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const PackageSettings = () => {
  const { toast } = useToast();
  const [packages, setPackages] = useState([
    {
      name: 'Basic',
      price: 499,
      originalPrice: 999,
      products: 1,
      margin: '10-20%',
      campaigns: 1,
      features: ['1 Winning Product', 'Shopify Store Setup', 'Product Listing', '1 Ad Campaign', 'Order Management', 'WhatsApp Support'],
      notIncluded: ['No Ads Credits', 'No Dedicated Manager'],
      badge: 'STARTER',
      popular: false
    },
    {
      name: 'Advanced',
      price: 899,
      originalPrice: 1999,
      products: 2,
      margin: '20-35%',
      campaigns: 3,
      adsCredit: 500,
      features: ['2 Winning Products', 'Premium Store Theme', 'Product Listing + SEO', '3 Ad Campaigns', '₹500 Ads Credit', 'Account Manager', 'Priority Support', 'Advanced Analytics'],
      notIncluded: [],
      badge: 'BEST VALUE',
      popular: true
    },
    {
      name: 'Professional',
      price: 1999,
      originalPrice: 3999,
      products: 5,
      margin: '35-60%',
      campaigns: 'Unlimited',
      adsCredit: 1500,
      features: ['5 Premium Products', 'Custom Domain + Store', 'Full SEO Optimization', 'Unlimited Campaigns', '₹1500 Ads Credit', 'Senior Manager', '24/7 VIP Support', 'Premium Analytics', 'Growth Consultation'],
      notIncluded: [],
      badge: 'PREMIUM',
      popular: false
    }
  ]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingIndex, setEditingIndex] = useState(null);
  const [newFeature, setNewFeature] = useState('');

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/settings/packages`);
      if (response.data && response.data.length > 0) {
        setPackages(response.data);
      }
    } catch (error) {
      console.error('Error fetching packages:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/admin/settings/packages`, { packages });
      toast({
        title: '✅ Success',
        description: 'Package settings updated successfully',
      });
    } catch (error) {
      toast({
        title: '❌ Error',
        description: 'Failed to update package settings',
        variant: 'destructive',
      });
    } finally {
      setSaving(false);
    }
  };

  const updatePackage = (index, field, value) => {
    const newPackages = [...packages];
    newPackages[index][field] = value;
    setPackages(newPackages);
  };

  const addFeature = (packageIndex) => {
    if (newFeature.trim()) {
      const newPackages = [...packages];
      newPackages[packageIndex].features.push(newFeature.trim());
      setPackages(newPackages);
      setNewFeature('');
    }
  };

  const removeFeature = (packageIndex, featureIndex) => {
    const newPackages = [...packages];
    newPackages[packageIndex].features.splice(featureIndex, 1);
    setPackages(newPackages);
  };

  if (loading) {
    return <div className="flex justify-center py-12"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div></div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-slate-800">Package Settings</h2>
          <p className="text-slate-600">Manage pricing packages displayed on the landing page</p>
        </div>
        <Button onClick={handleSave} disabled={saving} className="bg-blue-600 hover:bg-blue-700">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {packages.map((pkg, pkgIndex) => (
          <div key={pkgIndex} className={`bg-white rounded-xl border-2 p-6 ${pkg.popular ? 'border-blue-500 shadow-lg' : 'border-slate-200'}`}>
            {/* Popular Badge */}
            {pkg.popular && (
              <div className="bg-blue-600 text-white text-xs font-bold py-1 px-3 rounded-full mb-3 inline-block">
                MOST POPULAR
              </div>
            )}

            {/* Name */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Package Name</label>
              <input
                type="text"
                value={pkg.name}
                onChange={(e) => updatePackage(pkgIndex, 'name', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 font-bold text-lg"
              />
            </div>

            {/* Pricing */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Price (₹)</label>
                <input
                  type="number"
                  value={pkg.price}
                  onChange={(e) => updatePackage(pkgIndex, 'price', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Original Price (₹)</label>
                <input
                  type="number"
                  value={pkg.originalPrice}
                  onChange={(e) => updatePackage(pkgIndex, 'originalPrice', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Products & Margin */}
            <div className="grid grid-cols-2 gap-3 mb-4">
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Products</label>
                <input
                  type="number"
                  value={pkg.products}
                  onChange={(e) => updatePackage(pkgIndex, 'products', parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="text-xs font-medium text-slate-600 mb-1 block">Margin</label>
                <input
                  type="text"
                  value={pkg.margin}
                  onChange={(e) => updatePackage(pkgIndex, 'margin', e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>

            {/* Badge */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 mb-1 block">Badge Text</label>
              <input
                type="text"
                value={pkg.badge}
                onChange={(e) => updatePackage(pkgIndex, 'badge', e.target.value)}
                className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
            </div>

            {/* Features */}
            <div className="mb-4">
              <label className="text-xs font-medium text-slate-600 mb-2 block">Features</label>
              <div className="space-y-2 mb-2">
                {pkg.features.map((feature, featureIndex) => (
                  <div key={featureIndex} className="flex items-center gap-2">
                    <input
                      type="text"
                      value={feature}
                      onChange={(e) => {
                        const newPackages = [...packages];
                        newPackages[pkgIndex].features[featureIndex] = e.target.value;
                        setPackages(newPackages);
                      }}
                      className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={() => removeFeature(pkgIndex, featureIndex)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={editingIndex === pkgIndex ? newFeature : ''}
                  onChange={(e) => setNewFeature(e.target.value)}
                  onFocus={() => setEditingIndex(pkgIndex)}
                  placeholder="Add new feature..."
                  className="flex-1 px-2 py-1 text-sm border border-slate-200 rounded focus:ring-2 focus:ring-blue-500"
                />
                <button
                  onClick={() => addFeature(pkgIndex)}
                  className="text-blue-600 hover:text-blue-700"
                >
                  <Plus className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Popular Toggle */}
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={pkg.popular}
                onChange={(e) => updatePackage(pkgIndex, 'popular', e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-sm text-slate-600">Mark as Popular</span>
            </label>
          </div>
        ))}
      </div>

      {/* Preview */}
      <div className="bg-white rounded-xl border border-slate-200 p-6">
        <h3 className="text-lg font-semibold text-slate-800 mb-4">Preview</h3>
        <div className="text-sm text-slate-600">
          Changes will be reflected on the landing page after saving. Total packages: {packages.length}
        </div>
      </div>
    </div>
  );
};

export default PackageSettings;
